// src/pages/Notifications.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  ButtonGroup,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import notificationService from '../services/notificationService';
import { useSnackbar } from 'notistack';
import { useThemeSettings } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { NotificationSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';

const Notifications = () => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    loadNotifications();
  }, [filter, priorityFilter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const includeRead = filter === 'all' || filter === 'read';
      const response = await notificationService.getNotifications({
        limit: 100,
        includeRead,
        priority: priorityFilter
      });

      let filteredNotifications = response.notifications || [];

      // Apply read/unread filter
      if (filter === 'unread') {
        filteredNotifications = filteredNotifications.filter(n => !n.isRead);
      } else if (filter === 'read') {
        filteredNotifications = filteredNotifications.filter(n => n.isRead);
      }

      setNotifications(filteredNotifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setError(error);
      enqueueSnackbar('Failed to load notifications', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      loadNotifications();
      enqueueSnackbar('Notification marked as read', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to mark as read', { variant: 'error' });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      loadNotifications();
      enqueueSnackbar('All notifications marked as read', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to mark all as read', { variant: 'error' });
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate if action URL exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
      case 'urgent':
        return <ErrorIcon color="error" />;
      case 'high':
        return <WarningIcon color="warning" />;
      case 'normal':
        return <InfoIcon color="info" />;
      case 'low':
        return <CheckCircleIcon color="success" />;
      default:
        return <InfoIcon />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'info';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const filteredBySearch = notifications.filter(notification => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      notification.title?.toLowerCase().includes(query) ||
      notification.body?.toLowerCase().includes(query)
    );
  });

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {t ? t('Notifications.title') : 'Notifications'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadNotifications}
            variant="outlined"
          >
            {t ? t('DeviceStatusDashboard.refresh') : 'Refresh'}
          </Button>
          {unreadCount > 0 && (
            <Button
              startIcon={<MarkEmailReadIcon />}
              onClick={handleMarkAllRead}
              variant="contained"
            >
              {t ? t('Notifications.mark_all_read') : 'Mark All Read'} ({unreadCount})
            </Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* Filters and Search */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t ? t('Search.search') : 'Search notifications...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />

          <ButtonGroup size="small">
            <Button
              variant={filter === 'all' ? 'contained' : 'outlined'}
              onClick={() => setFilter('all')}
            >
              {t ? t('Notifications.all') : 'All'} ({notifications.length})
            </Button>
            <Button
              variant={filter === 'unread' ? 'contained' : 'outlined'}
              onClick={() => setFilter('unread')}
            >
              {t ? t('Notifications.unread') : 'Unread'} ({unreadCount})
            </Button>
            <Button
              variant={filter === 'read' ? 'contained' : 'outlined'}
              onClick={() => setFilter('read')}
            >
              {t ? t('Notifications.read') : 'Read'} ({notifications.filter(n => n.isRead).length})
            </Button>
          </ButtonGroup>

          <Button
            size="small"
            startIcon={<FilterIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            variant="outlined"
          >
            {priorityFilter ? priorityFilter.toUpperCase() : (t ? t('Notifications.priority') : 'Priority')}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => { setPriorityFilter(null); setAnchorEl(null); }}>
              {t ? t('Notifications.all_priorities') : 'All Priorities'}
            </MenuItem>
            <MenuItem onClick={() => { setPriorityFilter('critical'); setAnchorEl(null); }}>
              {t ? t('Notifications.critical_urgent') : 'Critical/Urgent'}
            </MenuItem>
            <MenuItem onClick={() => { setPriorityFilter('high'); setAnchorEl(null); }}>
              {t ? t('Notifications.high') : 'High'}
            </MenuItem>
            <MenuItem onClick={() => { setPriorityFilter('normal'); setAnchorEl(null); }}>
              {t ? t('Notifications.normal') : 'Normal'}
            </MenuItem>
            <MenuItem onClick={() => { setPriorityFilter('low'); setAnchorEl(null); }}>
              {t ? t('Notifications.low') : 'Low'}
            </MenuItem>
          </Menu>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Error Message */}
        {error && (
          <ErrorMessage
            error={error}
            title={t ? t('Notifications.error_loading') : 'Failed to load notifications'}
            context="notifications"
            onRetry={loadNotifications}
            showDetails={true}
          />
        )}

        {/* Notifications List */}
        {loading ? (
          <NotificationSkeleton count={5} />
        ) : !error && filteredBySearch.length === 0 ? (
          <EmptyState
            type={searchQuery ? 'search' : 'notifications'}
            title={searchQuery
              ? (t ? t('Notifications.no_matching_notifications') : 'No matching notifications')
              : undefined}
            description={searchQuery
              ? (t ? t('Notifications.search_description') : 'Try adjusting your search terms or filters to find notifications.')
              : undefined}
            secondaryAction={{
              label: t ? t('DeviceStatusDashboard.refresh') : 'Refresh',
              onClick: loadNotifications,
              icon: <RefreshIcon />
            }}
          />
        ) : !error && (
          <List sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            {filteredBySearch.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  disablePadding
                  sx={{
                    backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  <ListItemButton
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ py: 2 }}
                  >
                    <ListItemIcon>
                      {getPriorityIcon(notification.priority)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: notification.isRead ? 400 : 600 }}
                          >
                            {notification.title}
                          </Typography>
                          {(notification.priority === 'critical' || notification.priority === 'urgent') && (
                            <Chip
                              label={notification.priority.toUpperCase()}
                              size="small"
                              color="error"
                              sx={{ height: 20 }}
                            />
                          )}
                          {!notification.isRead && (
                            <Chip
                              label="NEW"
                              size="small"
                              color="primary"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {notification.body}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </Typography>
                        </>
                      }
                    />
                    {!notification.isRead && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notification.id);
                        }}
                        size="small"
                      >
                        <MarkEmailReadIcon />
                      </IconButton>
                    )}
                  </ListItemButton>
                </ListItem>
                {index < filteredBySearch.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Notifications;
